<script setup lang="ts">
const { $trpc } = useNuxtApp();
const { data: useCases, pending, error } = await $trpc.useCases.list.useQuery();

const simpleCases = computed(() => useCases.value?.filter((c: any) => c.category === 'Simple') || []);
const advancedCases = computed(() => useCases.value?.filter((c: any) => c.category === 'Advanced') || []);
</script>

<template>
  <div>
    <div class="max-w-6xl mx-auto py-16 px-6">
      <div class="text-center mb-16">
        <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-4">Powerful Use Cases For Every Need</h1>
        <p class="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Explore how users across different industries use spreadsheet automation to streamline their daily workflows.
        </p>
      </div>

      <div v-if="pending" class="py-20 text-center">
        <UIcon name="i-heroicons-arrow-path" class="animate-spin text-4xl text-primary" />
      </div>

      <div v-else-if="error" class="py-20 text-center bg-red-500/10 text-red-500 rounded-xl border border-red-500/30">
         <h3 class="text-2xl font-bold mb-2">TRPC Router Error</h3>
         <p>{{ error.message || String(error) }}</p>
      </div>

      <div v-else>
        <!-- Simple Category -->
        <div class="mb-16">
          <div class="flex items-center gap-4 mb-8">
            <h2 class="text-2xl font-bold">Simple Automations</h2>
            <UBadge color="primary" variant="soft">No external APIs</UBadge>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <NuxtLink v-for="caseItem in simpleCases" :key="caseItem.id" :to="`/use-cases/${caseItem.slug}`" class="block h-full group">
              <UCard class="flex flex-col h-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 transition-all group-hover:border-primary-500 group-hover:shadow-lg">
                <template #header>
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="caseItem.bgColor">
                      <UIcon :name="caseItem.icon" class="text-xl" :class="caseItem.color" />
                    </div>
                    <div>
                      <h3 class="font-bold text-gray-900 dark:text-white text-lg transition-colors group-hover:text-primary-500">{{ caseItem.title }}</h3>
                      <p class="text-sm text-gray-500 mt-1 line-clamp-2">{{ caseItem.description }}</p>
                    </div>
                  </div>
                </template>
                <div class="bg-gray-950 p-4 rounded-lg overflow-x-auto text-xs font-mono text-green-400 border border-gray-800 leading-relaxed shadow-inner max-h-48">
                  <pre>{{ caseItem.code }}</pre>
                </div>
              </UCard>
            </NuxtLink>
          </div>
        </div>

        <USeparator class="my-16" />

        <!-- Advanced Category -->
        <div>
          <div class="flex items-center gap-4 mb-8">
            <h2 class="text-2xl font-bold">Advanced Integrations</h2>
            <UBadge color="warning" variant="soft">Uses E2E Vault Credentials</UBadge>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <NuxtLink v-for="caseItem in advancedCases" :key="caseItem.id" :to="`/use-cases/${caseItem.slug}`" class="block h-full group">
              <UCard class="flex flex-col h-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 transition-all group-hover:border-warning-500 group-hover:shadow-lg">
                <template #header>
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="caseItem.bgColor">
                      <UIcon :name="caseItem.icon" class="text-xl" :class="caseItem.color" />
                    </div>
                    <div>
                      <h3 class="font-bold text-gray-900 dark:text-white text-lg transition-colors group-hover:text-warning-500">{{ caseItem.title }}</h3>
                      <p class="text-sm text-gray-500 mt-1 line-clamp-2">{{ caseItem.description }}</p>
                    </div>
                  </div>
                </template>
                <div class="bg-gray-950 p-4 rounded-lg overflow-x-auto text-xs font-mono text-blue-300 border border-gray-800 leading-relaxed shadow-inner max-h-48">
                  <pre>{{ caseItem.code }}</pre>
                </div>
              </UCard>
            </NuxtLink>
          </div>
        </div>
      </div>
      
      <!-- CTA Banner -->
      <div class="mt-20 p-8 text-center rounded-3xl bg-gradient-to-br from-primary-600 to-indigo-600 text-white shadow-xl relative overflow-hidden">
        <h2 class="text-2xl font-bold mb-4">Start Writing Your Own Templates</h2>
        <p class="text-primary-100 mb-8 max-w-xl mx-auto">Get free access to the LLM bindings and test out any of the templates above natively in the browser sandbox!</p>
        <UButton class="bg-white text-gray-900 hover:bg-gray-100" variant="solid" size="xl" to="/auth/signup">Get Started For Free</UButton>
      </div>
    </div>
  </div>
</template>
